import type { ReactNode } from 'react'

import { ChevronRight } from 'lucide-react'

import { CompositionDonut } from '../CompositionDonut'

import type { PieSegment } from '../../../lib/composition'

/** White card shell with the dashboard's uppercase section heading + optional "open lens" action. */
export function DashCard({ title, caption, onOpen, openLabel, children }: {
  title: string
  /** Small italic note on the right of the heading (e.g. "District-wide"). */
  caption?: string
  /** Clicking the header chevron jumps to the tab that answers this card in full. */
  onOpen?: () => void
  openLabel?: string
  children: ReactNode
}) {
  return (
    <section className="min-w-0 rounded-[12px] border border-gis-line bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[10px] font-extrabold tracking-wider text-gis-muted uppercase">{title}</h3>
        <span className="flex items-center gap-2">
          {caption && <span className="text-[10px] font-medium text-gis-muted italic">{caption}</span>}
          {onOpen && (
            <button
              type="button"
              onClick={onOpen}
              aria-label={openLabel ?? `Open ${title}`}
              title={openLabel}
              className="grid h-[22px] w-[22px] place-items-center rounded-[7px] text-gis-soft transition-colors hover:bg-gis-track hover:text-gis-ink focus-visible:ring-2 focus-visible:ring-blue-500/60"
            >
              <ChevronRight className="h-[14px] w-[14px]" />
            </button>
          )}
        </span>
      </div>
      {children}
    </section>
  )
}

/**
 * One clickable KPI stat tile: big number (+ optional period delta — the collector's first
 * question is "better or worse?"), label, muted context line. The tile IS the shortcut —
 * clicking applies the matching filter and jumps to the lens that answers it in detail.
 */
export function KpiTile({ value, label, sub, tone, delta, onClick }: {
  value: string
  label: string
  sub: string
  /** Number color — 'ink' default; 'red' flags the bad-news tiles only when non-zero. */
  tone?: 'ink' | 'red'
  /** Period-over-period movement (a `DeltaBadge`) — omitted when the series can't support one. */
  delta?: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group min-w-0 rounded-[12px] border border-gis-line bg-white px-3 py-[10px] text-left transition-colors hover:border-[#c7d2ea] hover:bg-[#fbfcfe] focus-visible:ring-2 focus-visible:ring-blue-500/60"
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className={`block text-[22px] leading-none font-bold tracking-[-.01em] tabular-nums @[620px]/dash:text-[25px] ${tone === 'red' ? 'text-gis-red' : 'text-gis-ink'}`}>
          {value}
        </span>
        {delta}
      </span>
      <span className="mt-[5px] flex items-center gap-1 text-[11px] font-semibold text-gis-ink-2">
        {label}
        <ChevronRight className="h-[11px] w-[11px] text-gis-muted opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
      </span>
      <span className="mt-[2px] block truncate text-[10.5px] text-gis-soft">{sub}</span>
    </button>
  )
}

/**
 * Donut + legend card. Legend rows carry label + count + share (visible labels — color is never the
 * only signal; the app's status greys are intentionally muted). Clicking an arc or a legend row
 * applies that value as a filter on the target board and jumps to it.
 */
export function DonutCard({ title, segments, centerValue, centerLabel, active, onPick, onOpen, openLabel }: {
  title: string
  segments: PieSegment[]
  centerValue: string
  centerLabel: string
  /** Selected keys to spotlight (arcs + legend rows). Omit for a plain donut. */
  active?: readonly string[]
  /** Click an arc/legend row → filter the target board by this key + jump to it. */
  onPick: (key: string) => void
  onOpen: () => void
  openLabel: string
}) {
  const total = segments.reduce((a, s) => a + s.count, 0)
  const activeSet = active ?? []
  return (
    <DashCard title={title} onOpen={onOpen} openLabel={openLabel}>
      <div className="flex items-center gap-3">
        <CompositionDonut
          segments={segments}
          total={total}
          centerValue={centerValue}
          centerLabel={centerLabel}
          active={activeSet}
          size={112}
          gap={2}
          centerValueSize={26}
          onToggle={onPick}
        />
        <div className="min-w-0 flex-1">
          {segments.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => onPick(s.key)}
              aria-pressed={activeSet.includes(s.key)}
              className={`-mx-1 flex w-full cursor-pointer items-center justify-between gap-2 rounded-[8px] px-1 py-[4px] text-left transition-colors focus-visible:ring-2 focus-visible:ring-blue-500/60 ${activeSet.includes(s.key) ? 'bg-[#eef3fb]' : 'hover:bg-[#f5f7fa]'}`}
            >
              <span className="flex min-w-0 items-center gap-2 text-[11.5px] font-semibold text-gis-ink-2">
                <span className={`h-[9px] w-[9px] flex-none rounded-[3px] ring-2 ${activeSet.includes(s.key) ? 'ring-gis-navy/40' : 'ring-transparent'}`} style={{ background: s.color }} />
                <span className="truncate">{s.label}</span>
              </span>
              <span className="flex flex-none items-center gap-[6px]">
                <span className="min-w-[18px] text-right text-[11.5px] font-bold tabular-nums text-gis-ink">{s.count}</span>
                <span className="min-w-[32px] text-right text-[10px] tabular-nums text-gis-muted">
                  ({total > 0 ? Math.round((s.count / total) * 100) : 0}%)
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </DashCard>
  )
}
